/**
 * Catalog queries and the events that write to it.
 *
 * Search loads the whole product table and filters in memory. At the target scale — a few
 * hundred to about fifteen hundred SKUs — that is roughly a millisecond, and it buys
 * accent-insensitive, any-order, mid-word matching that an IndexedDB prefix index cannot do.
 * If this ever holds tens of thousands of products it needs a real index; it will not.
 */

import {
  CODIGO_MAX,
  ZERO,
  addQty,
  centavos,
  clavesCodigo,
  costOf,
  limpiarCodigo,
  mismoCodigo,
  newId,
  type Centavos,
  type Milli,
  type UnitCost,
} from '@abastece/shared';
import { ZERO_COST, ZERO_QTY } from '@abastece/shared';
import { precioId, type Existencia, type ListaPrecio, type Producto, type UnidadVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { localeActual, traducir } from '../i18n/index.js';
import { nombreProducto, nombresDe, type ConNombre } from '../i18n/productos.js';
import { enRango } from './costos.js';
import { emitir } from './emitir.js';
import { coincide, normalizar, puntaje } from './texto.js';

/**
 * Whether a product answers to what was typed, in either language.
 *
 * The terms are matched against both names at once, so a search does not have to pick a
 * language — «arroz», "rice" and even «arroz rice» all find the same sack.
 */
export function coincideProducto(p: ConNombre, consulta: string): boolean {
  return coincide(nombresDe(p).join(' '), consulta);
}

/** Best rank across its names: typing "rice" puts "Rice 80/20" first on a Spanish screen too. */
const puntajeProducto = (p: ConNombre, consulta: string) =>
  Math.min(...nombresDe(p).map((n) => puntaje(n, consulta)));

/** Whether any of these codes shares a key with `claves` (`clavesCodigo` of the one read). */
function algunaClave(codigos: readonly string[] | undefined, claves: readonly string[]): boolean {
  return claves.length > 0 && (codigos ?? []).some((c) => clavesCodigo(c).some((k) => claves.includes(k)));
}

function tieneClave(p: Producto, claves: readonly string[]): boolean {
  return algunaClave(p.codigosBarras, claves);
}

/** Sale units that answer to a code: a case's barcode, printed on the box. */
export function unidadesConCodigo(unidades: readonly UnidadVenta[], codigo: string): UnidadVenta[] {
  const claves = clavesCodigo(codigo);
  return unidades.filter((u) => algunaClave(u.codigosBarras, claves));
}

/**
 * What a code rings up: each product that answers to it, with the presentation when the code is
 * one presentation's own. That wins over the product's own codes, which ring up its smallest
 * unit: the box's code on the box, the can's on the can.
 */
export function lecturaDeCodigo(
  productos: readonly Producto[],
  unidades: readonly UnidadVenta[],
  codigo: string,
  opciones: { inactivos?: boolean } = {},
): { producto: Producto; unidad?: UnidadVenta }[] {
  const porUnidad = unidadesConCodigo(unidades, codigo).flatMap((unidad) => {
    const producto = productos.find((p) => p.id === unidad.productoId && (opciones.inactivos || p.activo));
    return producto ? [{ producto, unidad }] : [];
  });
  const yaEstan = new Set(porUnidad.map((h) => h.producto.id));
  return [
    ...porUnidad,
    ...productosConCodigo(productos, codigo, opciones)
      .filter((p) => !yaEstan.has(p.id))
      .map((producto) => ({ producto })),
  ];
}

/**
 * `lecturaDeCodigo` off sale too, for a screen where what is on the shelf is stock whether it is sold
 * or not: «Inventario» (`productosDelCodigo`), and a count since 2026-10-18. A product on sale with
 * the code is the one meant, over one taken off sale that still has it: the duplicate deactivated
 * for the other keeps the code they shared, and a count asked «¿Cuál es?» at every scan of it.
 */
export function lecturaConDesactivados(
  productos: readonly Producto[],
  unidades: readonly UnidadVenta[],
  codigo: string,
): { producto: Producto; unidad?: UnidadVenta }[] {
  const todos = lecturaDeCodigo(productos, unidades, codigo, { inactivos: true });
  const aLaVenta = todos.filter(({ producto }) => producto.activo);
  return aLaVenta.length ? aLaVenta : todos;
}

/**
 * `nombre` is how the result will be shown, so the list is in the order the eye reads it —
 * alphabetical by English name on an English screen.
 *
 * A typed code finds its product however it was spelled when it was saved, and comes first:
 * whoever typed thirteen digits meant that product and not one whose name contains them.
 */
export function buscar(
  productos: readonly Producto[],
  consulta: string,
  limite = 60,
  nombre: (p: ConNombre) => string = nombreProducto,
  /** For a presentation's own code to find its product too. */
  unidades: readonly UnidadVenta[] = [],
): Producto[] {
  return buscarEntre(productos.filter((p) => p.activo), consulta, limite, nombre, unidades);
}

/**
 * `buscar` among whichever products it is handed, on sale or not: «Desactivados» in Productos
 * searches the ones taken off sale with it, and a count both (2026-10-18), the one on sale first of
 * two that answer alike.
 */
export function buscarEntre(
  productos: readonly Producto[],
  consulta: string,
  limite = 60,
  nombre: (p: ConNombre) => string = nombreProducto,
  unidades: readonly UnidadVenta[] = [],
): Producto[] {
  const locale = localeActual();
  const porNombre = (a: Producto, b: Producto) => nombre(a).localeCompare(nombre(b), locale);
  if (!consulta.trim()) return [...productos].sort(porNombre).slice(0, limite);
  const claves = clavesCodigo(consulta);
  const deUnidades = new Set(unidades.filter((u) => algunaClave(u.codigosBarras, claves)).map((u) => u.productoId));
  const porCodigo = new Set(productos.filter((p) => tieneClave(p, claves) || deUnidades.has(p.id)));
  return productos
    .filter((p) => porCodigo.has(p) || coincideProducto(p, consulta))
    .sort(
      (a, b) =>
        Number(porCodigo.has(b)) - Number(porCodigo.has(a)) ||
        puntajeProducto(a, consulta) - puntajeProducto(b, consulta) ||
        Number(!a.activo) - Number(!b.activo) ||
        porNombre(a, b),
    )
    .slice(0, limite);
}

/**
 * The products that answer to a scanned code, however either side spelled it.
 *
 * Usually one. None is a code the shop has not met yet. More than one is two products carrying
 * the same code — somebody registered the same goods twice, or two phones each taught it a
 * code while offline — and the person scanning is asked which they meant rather than having
 * the app guess. Inactive products only on request: a product taken off sale should not ring
 * up, but a code being given to a new product should still say where it was before.
 */
export function productosConCodigo(
  productos: readonly Producto[],
  codigo: string,
  { inactivos = false }: { inactivos?: boolean } = {},
): Producto[] {
  const claves = clavesCodigo(codigo);
  return productos.filter((p) => (inactivos || p.activo) && tieneClave(p, claves));
}

/** Stock across every location, with a value-weighted cost. What an inventory report shows. */
export function totalizar(existencias: readonly Existencia[]): {
  cantidad: Milli;
  costo: UnitCost;
  valor: Centavos;
} {
  const cantidad = existencias.reduce<Milli>((a, e) => addQty(a, e.cantidad), ZERO_QTY);
  const valor = existencias.reduce<number>((a, e) => a + costOf(e.costoPromedio, e.cantidad), 0);
  const costo = (cantidad > 0 ? Math.round((valor * 1_000 * 1_000) / cantidad) : 0) as UnitCost;
  return { cantidad, costo: costo || ZERO_COST, valor: (valor || 0) as Centavos };
}

export function precioDe(
  precios: readonly { id: string; precio: Centavos }[],
  unidadVentaId: string,
  listaId: string,
): Centavos | null {
  const id = precioId(unidadVentaId, listaId);
  return precios.find((p) => p.id === id)?.precio ?? null;
}

/**
 * What a new presentation of a product starts at on each list (the owner, 2026-10-18): as much as it
 * holds of the smallest presentation priced there, at that one's price, so a box of 12 is 12 × the
 * packet's, rounded half up to the centavo as a line's total is. Changed in the table like any
 * price. A list none of the product's presentations has a price on gives none, and so does one the
 * books could not take.
 */
export function preciosParaNueva(
  precios: readonly { id: string; precio: Centavos }[],
  suyas: readonly UnidadVenta[],
  listas: readonly Pick<ListaPrecio, 'id'>[],
  factor: Milli,
): { listaId: string; precio: Centavos; desde: UnidadVenta }[] {
  const enOrden = [...suyas].sort((a, b) => a.factor - b.factor);
  return listas.flatMap((l) => {
    for (const desde of enOrden) {
      const suyo = precioDe(precios, desde.id, l.id);
      if (suyo === null) continue;
      const veces = (BigInt(suyo) * BigInt(factor) * 2n + BigInt(desde.factor)) / (2n * BigInt(desde.factor));
      const precio = veces > 0n ? enRango(() => centavos(Number(veces))) : null;
      return precio === null ? [] : [{ listaId: l.id, precio, desde }];
    }
    return [];
  });
}

/** Ordered first, so every screen agrees which one it is even if two claim the flag. */
export const listaPredeterminada = (listas: readonly ListaPrecio[]): ListaPrecio | undefined => {
  const ordenadas = ordenarListas(listas);
  return ordenadas.find((l) => l.esPredeterminada) ?? ordenadas[0];
};

/**
 * What a price list is called on screen.
 *
 * The two lists every shop starts with are shown by what they are, in both languages: on their
 * own, «Público» and «Mayorista» read as an audience and a kind of customer, and nothing says
 * they are prices. Matched by the name they were created with, so a list the shop renamed, or
 * made itself, keeps the name somebody typed, like every other word the shop wrote.
 */
export function etiquetaListaPrecio(lista: { nombre: string }): string {
  switch (normalizar(lista.nombre)) {
    case 'publico':
      return traducir('lista.publico');
    case 'mayorista':
      return traducir('lista.mayorista');
    default:
      return lista.nombre;
  }
}

/**
 * A price list's name where there is room for a word, on each line of a sale (2026-10-11): beside
 * «Cobrar a», which says what they are, «Público» and «Mayorista» are prices already. A list the
 * shop named keeps its name, as above.
 */
export function etiquetaCortaLista(lista: { nombre: string }): string {
  switch (normalizar(lista.nombre)) {
    case 'publico':
      return traducir('lista.publicoCorta');
    case 'mayorista':
      return traducir('lista.mayoristaCorta');
    default:
      return lista.nombre;
  }
}

export function ordenarListas(listas: readonly ListaPrecio[]): ListaPrecio[] {
  return [...listas]
    .filter((l) => l.activa)
    .sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre, 'es') || (a.id < b.id ? -1 : 1));
}

export function unidadesDe(unidades: readonly UnidadVenta[], productoId: string): UnidadVenta[] {
  return unidades.filter((u) => u.productoId === productoId).sort((a, b) => a.factor - b.factor);
}

// ---------------------------------------------------------------------------
// Escritura
// ---------------------------------------------------------------------------

export interface NuevoProducto {
  nombre: string;
  /** Only if somebody wrote one. Never filled in by guessing. */
  nombreEn?: string;
  categoria: string;
  unidadBase: string;
  esMedicamento?: boolean;
  perecedero?: boolean;
  registroSanitario?: string;
  codigosBarras?: string[];
  unidadVentaNombre?: string;
  factor?: Milli;
  precios?: { listaId: string; precio: Centavos }[];
  /** A photo already stored on this device (`lib/fotos.ts`), by name. */
  foto?: string;
}

export async function crearProducto(p: NuevoProducto): Promise<{ productoId: string; unidadVentaId: string }> {
  const productoId = newId();
  const unidadVentaId = newId();

  const codigos = [...new Set((p.codigosBarras ?? []).map(limpiarCodigo).filter(Boolean))];
  if (codigos.some((c) => c.length > CODIGO_MAX)) {
    throw new Error(traducir('codigo.muyLargo', { max: CODIGO_MAX }));
  }

  const nombreEn = p.nombreEn?.trim();
  await emitir('producto.creado', {
    productoId,
    nombre: p.nombre.trim(),
    ...(nombreEn ? { nombreEn } : {}),
    categoria: p.categoria || 'Otros',
    unidadBase: p.unidadBase,
    esMedicamento: p.esMedicamento ?? false,
    perecedero: p.perecedero ?? false,
    ...(p.registroSanitario ? { registroSanitario: p.registroSanitario } : {}),
    codigosBarras: codigos,
    unidadVenta: {
      unidadVentaId,
      nombre: p.unidadVentaNombre ?? p.unidadBase,
      factor: p.factor ?? 1_000,
    },
    precios: (p.precios ?? []).filter((x) => x.precio > 0),
    ...(p.foto ? { foto: p.foto } : {}),
  });

  return { productoId, unidadVentaId };
}

/**
 * Teaches a product one more code.
 *
 * Read from the database rather than taken from the caller, because the list is replaced as a
 * whole: a product handed over by a screen rendered a moment ago can be missing the code the
 * scan before this one just added. A code the product already answers to, in any spelling, is
 * left alone.
 */
export async function agregarCodigo(productoId: string, codigo: string): Promise<void> {
  const limpio = limpiarCodigo(codigo);
  if (!limpio) return;
  if (limpio.length > CODIGO_MAX) throw new Error(traducir('codigo.muyLargo', { max: CODIGO_MAX }));
  const producto = await db.productos.get(productoId);
  if (!producto || producto.codigosBarras.some((c) => mismoCodigo(c, limpio))) return;
  await emitir('producto.actualizado', { productoId, codigosBarras: [...producto.codigosBarras, limpio] });
}

/** Teaches one presentation a code of its own, the way `agregarCodigo` teaches a product. */
export async function agregarCodigoAUnidad(unidadVentaId: string, codigo: string): Promise<void> {
  const limpio = limpiarCodigo(codigo);
  if (!limpio) return;
  if (limpio.length > CODIGO_MAX) throw new Error(traducir('codigo.muyLargo', { max: CODIGO_MAX }));
  const unidad = await db.unidades.get(unidadVentaId);
  const actuales = unidad?.codigosBarras ?? [];
  if (!unidad || actuales.some((c) => mismoCodigo(c, limpio))) return;
  await emitir('unidad_venta.actualizada', { unidadVentaId, codigosBarras: [...actuales, limpio] });
}

/**
 * A code given to the product in the hand, on the presentation it is printed on: the smallest
 * presentation's code is the product's own, any other's is that presentation's. Gives back that
 * other presentation, when it was one: what a scan of the code now rings up.
 */
export async function ensenarCodigo(
  productoId: string,
  unidad: UnidadVenta | undefined,
  propias: readonly UnidadVenta[],
  codigo: string,
): Promise<UnidadVenta | undefined> {
  const deOtra = unidad && unidad.id !== propias[0]?.id ? unidad : undefined;
  if (deOtra) await agregarCodigoAUnidad(deOtra.id, codigo);
  else await agregarCodigo(productoId, codigo);
  return deOtra;
}

export async function quitarCodigoDeUnidad(unidadVentaId: string, codigo: string): Promise<void> {
  const unidad = await db.unidades.get(unidadVentaId);
  if (!unidad?.codigosBarras?.includes(codigo)) return;
  await emitir('unidad_venta.actualizada', {
    unidadVentaId,
    codigosBarras: unidad.codigosBarras.filter((c) => c !== codigo),
  });
}

export async function quitarCodigo(productoId: string, codigo: string): Promise<void> {
  const producto = await db.productos.get(productoId);
  if (!producto?.codigosBarras.includes(codigo)) return;
  await emitir('producto.actualizado', {
    productoId,
    codigosBarras: producto.codigosBarras.filter((c) => c !== codigo),
  });
}

/**
 * Takes products off sale: out of the till, the prices, «Por comprar», what the shop still has
 * to price before it opens, and every search, and their codes no longer ring them up. Nothing is
 * deleted: what was sold, bought and counted points at them, and that is the shop's books.
 * «Desactivados», in Productos, puts them back.
 *
 * One event each, so the log records who took which off, and when. Each is read first, and one
 * already off on this device's books (another device's doing, synced) is left alone; twice would
 * do no harm, only say it twice. `alAvanzar` counts them off, for a screen doing a hundred at once.
 */
export async function desactivarProductos(
  ids: readonly string[],
  alAvanzar?: (hechos: number) => void,
): Promise<void> {
  let hechos = 0;
  for (const productoId of ids) {
    if ((await db.productos.get(productoId))?.activo) await emitir('producto.desactivado', { productoId });
    alAvanzar?.(++hechos);
  }
}

/** Puts products taken off sale back on it, the same way. */
export async function reactivarProductos(
  ids: readonly string[],
  alAvanzar?: (hechos: number) => void,
): Promise<void> {
  let hechos = 0;
  for (const productoId of ids) {
    const producto = await db.productos.get(productoId);
    if (producto && !producto.activo) await emitir('producto.reactivado', { productoId });
    alAvanzar?.(++hechos);
  }
}

const LISTAS_BASE = [
  { nombre: 'Público', esPredeterminada: true, orden: 0 },
  { nombre: 'Mayorista', esPredeterminada: false, orden: 1 },
] as const;

/**
 * Creates Público and Mayorista on an installation that has neither.
 *
 * Checked name by name rather than "are there any lists at all". The count was enough right
 * up until something called this twice against a shop whose lists had not finished syncing
 * down, and an installation ended up with three lists called Público — all three flagged as
 * the default, only the first carrying any prices, and the other two silently pricing every
 * product at zero if anybody picked one.
 *
 * Still a read and still not a lock, so two devices doing this offline at the same instant can
 * both create a set. That one is genuinely rare and genuinely visible; seeding over yourself
 * was neither.
 */
export async function asegurarListasBase(): Promise<void> {
  const existentes = new Set((await db.listas.toArray()).map((l) => normalizar(l.nombre)));

  for (const base of LISTAS_BASE) {
    if (existentes.has(normalizar(base.nombre))) continue;
    await emitir('lista_precio.creada', { listaId: newId(), ...base });
  }
}

export { ZERO as CERO };
